import { Extension } from '@tiptap/core';
import { DOMSerializer } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';

// ============================================================================
// Utilities
// ============================================================================

export function generateNodeId(): string {
	return `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Generate a base64-encoded template for a generic block node.
 * For generic blocks, we copy the full content as-is.
 */
export function generateBlockTemplate(html: string): string {
	try {
		return btoa(unescape(encodeURIComponent(html)));
	} catch {
		// Fallback for any encoding issues
		return btoa(html);
	}
}

/**
 * Decode a base64 template back to HTML
 */
export function decodeBlockTemplate(template: string): string {
	try {
		return decodeURIComponent(escape(atob(template)));
	} catch {
		return atob(template);
	}
}

// ============================================================================
// Types
// ============================================================================

export interface RepeatableOptions {
	userId?: string;
}

// ============================================================================
// Extension
// ============================================================================

declare module '@tiptap/core' {
	interface Commands<ReturnType> {
		repeatable: {
			toggleRepeat: () => ReturnType;
		};
	}
}

export const Repeatable = Extension.create<RepeatableOptions>({
	name: 'repeatable',

	addOptions() {
		return {
			userId: undefined,
		};
	},

	addGlobalAttributes() {
		return [
			{
				// Apply to common block-level nodes
				types: ['paragraph', 'heading', 'bulletList', 'orderedList', 'blockquote'],
				attributes: {
					repeats: {
						default: false,
						parseHTML: (element) => element.getAttribute('data-repeats') === 'true',
						renderHTML: (attributes) => {
							if (!attributes.repeats) return {};
							return { 'data-repeats': 'true' };
						},
					},
					nodeId: {
						default: null,
						parseHTML: (element) => element.getAttribute('data-node-id'),
						renderHTML: (attributes) => {
							if (!attributes.nodeId) return {};
							return { 'data-node-id': attributes.nodeId };
						},
					},
					repeatTemplate: {
						default: null,
						parseHTML: (element) => element.getAttribute('data-repeat-template'),
						renderHTML: (attributes) => {
							if (!attributes.repeatTemplate) return {};
							return { 'data-repeat-template': attributes.repeatTemplate };
						},
					},
					copiedFrom: {
						default: null,
						parseHTML: (element) => element.getAttribute('data-copied-from'),
						renderHTML: (attributes) => {
							if (!attributes.copiedFrom) return {};
							return { 'data-copied-from': attributes.copiedFrom };
						},
					},
					copiedByUser: {
						default: null,
						parseHTML: (element) => element.getAttribute('data-copied-by-user'),
						renderHTML: (attributes) => {
							if (!attributes.copiedByUser) return {};
							return { 'data-copied-by-user': attributes.copiedByUser };
						},
					},
					originalNodeId: {
						default: null,
						parseHTML: (element) => element.getAttribute('data-original-node-id'),
						renderHTML: (attributes) => {
							if (!attributes.originalNodeId) return {};
							return { 'data-original-node-id': attributes.originalNodeId };
						},
					},
				},
			},
		];
	},

	addCommands() {
		return {
			toggleRepeat:
				() =>
				({ state, dispatch, editor }) => {
					const { from, to } = state.selection;
					const { tr } = state;
					let toggled = false;
					const userId = this.options.userId;

					// Find the first block node in the selection
					state.doc.nodesBetween(from, to, (node, pos) => {
						// Only process block nodes, skip doc, topic, and listItem nodes
						// We allow bulletList and orderedList (the parent list), but not individual listItems
						if (!node.isBlock || node.type.name === 'doc' || node.type.name === 'topic' || node.type.name === 'listItem') {
							return true; // Continue traversing
						}

						// Skip if we already toggled one
						if (toggled) return false;

						const currentlyRepeating = node.attrs.repeats === true;

						if (currentlyRepeating) {
							// Turn off repeating
							tr.setNodeMarkup(pos, undefined, {
								...node.attrs,
								repeats: false,
								nodeId: null,
								repeatTemplate: null,
								copiedByUser: null,
								originalNodeId: null,
							});
						} else {
							// Turn on repeating
							const nodeId = generateNodeId();
							// originalNodeId: use existing (if this was copied) or this node's new ID (first time)
							const originalNodeId = node.attrs.originalNodeId || nodeId;

							// Serialize the node to HTML for the template
							const tempDiv = document.createElement('div');
							const serializer = DOMSerializer.fromSchema(editor.schema);
							const dom = serializer.serializeNode(node);
							tempDiv.appendChild(dom);

							// Add the repeat attributes to the template HTML
							const firstChild = tempDiv.firstElementChild;
							if (firstChild) {
								firstChild.setAttribute('data-repeats', 'true');
								firstChild.setAttribute('data-node-id', nodeId);
								firstChild.setAttribute('data-original-node-id', originalNodeId);
							}

							const template = generateBlockTemplate(tempDiv.innerHTML);

							tr.setNodeMarkup(pos, undefined, {
								...node.attrs,
								repeats: true,
								nodeId,
								repeatTemplate: template,
								copiedByUser: userId || null,
								originalNodeId,
							});
						}

						toggled = true;
						return false; // Stop traversing
					});

					if (toggled && dispatch) {
						dispatch(tr);
						return true;
					}

					return false;
				},
		};
	},

	addProseMirrorPlugins() {
		const repeatableTypes = ['paragraph', 'heading', 'bulletList', 'orderedList', 'blockquote'];

		return [
			new Plugin({
				appendTransaction: (transactions, oldState, newState) => {
					// Only process if the document changed
					if (!transactions.some((tr) => tr.docChanged)) {
						return null;
					}

					const tr = newState.tr;
					let modified = false;

					// Check all repeatable nodes for empty content
					newState.doc.descendants((node, pos) => {
						// Skip non-repeatable node types
						if (!repeatableTypes.includes(node.type.name)) {
							return true;
						}

						// Only process nodes that are marked as repeating
						if (!node.attrs.repeats) {
							return true;
						}

						// Check if the node's content is empty
						const isEmpty = node.textContent.trim() === '';

						if (isEmpty) {
							// Clear repeat attributes
							tr.setNodeMarkup(pos, undefined, {
								...node.attrs,
								repeats: false,
								nodeId: null,
								repeatTemplate: null,
								copiedByUser: null,
								originalNodeId: null,
							});
							modified = true;
						}

						return true;
					});

					return modified ? tr : null;
				},
			}),
		];
	},
});
